import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button, FormItem, NativeSelect, Spinner } from 'elmo-elements';

import {
  useGetCurrentTenantProfileQuery,
  useGetVideoPreviewLinkQuery,
  useGetVocamChannelsQuery,
  useGetVocamChaptersQuery,
  useGetVocamModulesQuery,
  useGetVocamRegionsQuery,
  useUpdateFrameMutation,
} from '@/modules/services';
import { FrameDataType, VocamMetadataType } from '@/modules/types/frame';
import { ElearningParamType } from '../../../routes';

import { ContentFormType } from '../SlideForm';
import VideoPlayerHls from '@/components/VideoPlayerHls';
import './SlideSetup.scss';
import { FrameQueryType } from '@/modules/services/frames';
import { getModuleFormData, hasValidVocamDataIds } from '@/lib/util';
import { useDispatch } from 'react-redux';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

interface VocamFormPropsType {
  frameData: FrameDataType;
  onSave?: (data: Partial<ContentFormType>) => void;
}

type TFilteredTenantRegion = {
  label: string;
  value: number | string;
};

type FieldNameType =
  | 'bgVideoTitle'
  | 'bgVideo'
  | 'vocamMetadata.regionId'
  | 'vocamMetadata.channelId'
  | 'vocamMetadata.moduleId'
  | 'vocamMetadata.topicId';

const regionsLabel: { [key: string]: string } = {
  au: 'Australia',
  nz: 'New Zealand',
  gb: 'Great Britain',
  ca: 'Canada',
  caf: 'Canada (French)',
  us: 'USA',
};

const schema = yup.object().shape(
  {
    id: yup.string().uuid().notRequired(),
    elearningId: yup.string().uuid().notRequired(),
    isVideoFrame: yup.boolean(),
    // Make bgVideo required if NO vocamMetadata
    bgVideo: yup.string().when(['isVideoFrame', 'vocamMetadata'], {
      is: (isVideoFrame: boolean, vocamMetadata: VocamMetadataType) =>
        isVideoFrame && !hasValidVocamDataIds(vocamMetadata),
      then: () => yup.string().required('Content is required'),
    }),
    bgVideoTitle: yup.string().notRequired(),
    vocamMetadata: yup
      .object()
      .shape({
        regionId: yup.number(),
        channelId: yup.number(),
        moduleId: yup.number(),
        topicId: yup.number(),
      })
      .when(['isVideoFrame', 'bgVideo'], {
        // Make vocamMetadata required if NO bgVideo
        is: (isVideoFrame: boolean, bgVideo: string) =>
          isVideoFrame && !bgVideo,
        then: () =>
          yup.object().shape({
            regionId: yup
              .number()
              .min(1, 'Region id need to be selected')
              .required('Region id need to be selected'),
            channelId: yup
              .number()
              .min(1, 'Channel id need to be selected')
              .required('Channel id need to be selected'),
            moduleId: yup
              .number()
              .min(1, 'Module id need to be selected')
              .required('Module id need to be selected'),
            topicId: yup
              .number()
              .min(1, 'Topic id need to be selected')
              .required('Topic id need to be selected'),
          }),
      }),
  },
  // We list cyclic dependency - so yup can handle circular dependency of these two properties w/o resulting into error
  [['vocamMetadata', 'bgVideo']],
);

const VocamForm = ({
  frameData,
  onSave,
}: VocamFormPropsType): React.ReactElement => {
  const dispatch = useDispatch();
  const { elearningId, frameId } = useParams<ElearningParamType>();

  const {
    register,
    setValue,
    watch,
    resetField,
    handleSubmit,
    formState: { errors },
  } = useForm<ContentFormType>({
    resolver: yupResolver(schema),
    defaultValues: getModuleFormData({
      currentElearningId: elearningId,
      currentFrameId: frameId,
      currentValue: frameData,
    }),
  });

  const topic = watch('vocamMetadata.topicId') || 0;
  const region = watch('vocamMetadata.regionId') || 0;
  const channel = watch('vocamMetadata.channelId') || 0;
  const module = watch('vocamMetadata.moduleId') || 0;
  const bgVideo = watch('bgVideo') || '';

  const vocamRegions = useGetVocamRegionsQuery();
  const { data: tenantProfile, isLoading: tenantProfileIsLoading } =
    useGetCurrentTenantProfileQuery();
  const channels = useGetVocamChannelsQuery();
  const modules = useGetVocamModulesQuery(
    {
      channelId: channel,
      regionId: region,
    },
    { skip: !region || !channel },
  );
  const chapters = useGetVocamChaptersQuery(module, {
    skip: !module,
  });
  const videoPreviewLink = useGetVideoPreviewLinkQuery(
    { moduleId: module, topicId: topic },
    { skip: !module || !topic },
  );

  const [updateFrameQuery, updateFrameStatus] = useUpdateFrameMutation();

  const onSubmit = async (data: Partial<ContentFormType>) => {
    const frameDataToSubmit: Partial<FrameQueryType> = {
      ...data,
      vocamMetadata: {
        channelId: data.vocamMetadata?.channelId,
        regionId: data.vocamMetadata?.regionId,
        moduleId: data.vocamMetadata?.moduleId,
        topicId: data.vocamMetadata?.topicId,
      },
      // set to empty to be able to use vocam
      bgVideo: '',
      bgVideoTitle: '',
    };

    await updateFrameQuery(frameDataToSubmit);
    if (onSave) {
      onSave(data);
    }
  };

  useEffect(() => {
    if (
      updateFrameStatus.isSuccess &&
      !updateFrameStatus.isError &&
      updateFrameStatus.data
    ) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Slide Saved Successfully`,
        },
      });
    }

    if (updateFrameStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }
  }, [updateFrameStatus]);

  const handleFieldChange = (e: any, fieldName: FieldNameType) => {
    const value = e.target ? e.target.value : e;
    setValue(fieldName, value);
  };

  const getRegions = (): TFilteredTenantRegion[] => {
    // ["AU", "NZ"]
    const allowedRegions =
      tenantProfile?.configMetadata?.regions?.map((regionData) =>
        regionData.toLowerCase(),
      ) || [];

    // [{ culture: "", region: "AU", regionId: 123 }]
    const regions = vocamRegions.data || [];

    if (!allowedRegions.length || !regions.length) return [];

    const filteredRegions = regions.filter((regionData) =>
      allowedRegions.includes(regionData.region.toLowerCase()),
    );
    const regionsData = filteredRegions.map((filteredRegion) => ({
      // Static: {au: "Australia"}
      label: regionsLabel[filteredRegion.region] ?? filteredRegion.region,
      value: filteredRegion.regionId,
    }));

    return regionsData;
  };
  const regionDropdownOptions = useMemo(getRegions, [
    tenantProfile,
    vocamRegions.data,
  ]);

  if (!frameId || !frameData) return <div />;
  if (channels.isLoading || vocamRegions.isLoading || tenantProfileIsLoading)
    return <Spinner />;

  return (
    <>
      <FormItem
        label="Region"
        size="sm"
        className="vocam-video-filter-options"
        message={!bgVideo ? errors.vocamMetadata?.regionId?.message : ''}
        status={
          errors.vocamMetadata?.regionId && !bgVideo ? 'error' : 'default'
        }
      >
        <div className="region-filter-options">
          {regionDropdownOptions.map((regionData) => (
            <Button
              className={
                +region === +regionData.value ? `vocam-active` : 'vocam-option'
              }
              onClick={() =>
                setValue('vocamMetadata.regionId', +regionData.value)
              }
              key={`region-${regionData.value}`}
            >
              {regionData.label}
            </Button>
          ))}
        </div>
      </FormItem>
      <FormItem
        label="Category"
        size="sm"
        className="vocam-video-filter-options"
        message={!bgVideo ? errors.vocamMetadata?.channelId?.message : ''}
        status={
          errors.vocamMetadata?.channelId && !bgVideo ? 'error' : 'default'
        }
      >
        <NativeSelect
          {...register('vocamMetadata.channelId', {})}
          onChange={(e) => {
            handleFieldChange(+e, 'vocamMetadata.channelId');
            // resets module & chapter selection to none
            handleFieldChange(0, 'vocamMetadata.moduleId');
            handleFieldChange(0, 'vocamMetadata.topicId');
          }}
          value={channel || 0}
        >
          <NativeSelect.Option label="None" value={0} />
          {channels.data?.map((category) => (
            <NativeSelect.Option
              label={category.name}
              value={category.channelId}
              key={`category-${category.channelId}`}
            />
          ))}
        </NativeSelect>
      </FormItem>
      <FormItem
        label="Title"
        size="sm"
        className="vocam-video-filter-options"
        message={!bgVideo ? errors.vocamMetadata?.moduleId?.message : ''}
        status={
          errors.vocamMetadata?.moduleId && !bgVideo ? 'error' : 'default'
        }
      >
        <NativeSelect
          {...register('vocamMetadata.moduleId', {})}
          onChange={(e) => {
            handleFieldChange(+e, 'vocamMetadata.moduleId');
            // resets chapter selection to none
            handleFieldChange(0, 'vocamMetadata.topicId');
          }}
          value={module || 0}
        >
          <NativeSelect.Option label="None" value={0} />
          {channel !== 0 &&
            region !== 0 &&
            modules.data?.map((moduleData) => (
              <NativeSelect.Option
                label={moduleData.name}
                value={moduleData.id}
                key={`module-${moduleData.id}`}
              />
            ))}
        </NativeSelect>
      </FormItem>
      <FormItem
        label="Chapter"
        size="sm"
        className="vocam-video-filter-options"
        message={!bgVideo ? errors.vocamMetadata?.topicId?.message : ''}
        status={errors.vocamMetadata?.topicId && !bgVideo ? 'error' : 'default'}
      >
        <NativeSelect
          {...register('vocamMetadata.topicId', {})}
          onChange={(e) => {
            handleFieldChange(+e, 'vocamMetadata.topicId');
            // change BgVideo to blank
            resetField('bgVideo');
            resetField('bgVideoTitle');
          }}
          value={topic || 0}
        >
          <NativeSelect.Option label="None" value={0} />
          {channel !== 0 &&
            region !== 0 &&
            module !== 0 &&
            chapters.data?.map((chapter) => (
              <NativeSelect.Option
                label={chapter.name}
                value={chapter.id}
                key={`chapter-${chapter.id}`}
              />
            ))}
        </NativeSelect>
      </FormItem>
      {topic !== 0 && videoPreviewLink.data && (
        <FormItem label="Preview">
          <div className="vocam-preview">
            <VideoPlayerHls
              videoLink={videoPreviewLink.data || ''}
              useHls={true}
            />
          </div>
        </FormItem>
      )}

      <FormItem>
        <Button
          type="primary"
          htmlType="button"
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </FormItem>
    </>
  );
};

export default VocamForm;
